// Re-constitutes a slide's elements_json/contents_json from its live DOM after
// a manual edit. The editor manipulates the rendered .el tree directly (drag,
// resize, text, colour, add, delete) – this module is the write-back path that
// makes the JSON model, not the HTML string, the source of truth.
//

const SlideModelEditor = (() => {
    function clone(o) {
        if (o === undefined) return undefined;
        return JSON.parse(JSON.stringify(o));
    }

    function uniqueId(base, used) {
        if (!used.has(base)) { used.add(base); return base; }
        let i = 2;
        while (used.has(`${base}_${i}`)) i++;
        const out = `${base}_${i}`;
        used.add(out);
        return out;
    }

    // Slide-relative geometry of a node in 960x540 space. getBoundingClientRect
    // resolves nested group wrappers and CSS transforms for free; the stage's
    // CSS scale factor drops out because slide and element scale together.
    function domPos(node, slideEl) {
        const s = slideEl.getBoundingClientRect();
        const b = node.getBoundingClientRect();
        const scaleX = s.width ? s.width / 960 : 1;
        return {
            left: (b.left - s.left) / scaleX,
            top: (b.top - s.top) / scaleX,
            width: b.width / scaleX,
            height: b.height / scaleX,
        };
    }

    function readZ(domEl) {
        return parseInt(domEl.style.zIndex, 10) || 10;
    }

    // ─── Alignment ─────────────────────────────────────────────────────────
    // Element-level alignment is the box's own `align: {h, v}`
    // Caret-run alignment lives in the rich text as block-level
    // <p style="text-align:…">. The two granularities cascade:
    //   run > element align.h.
    //
    // A run-level value equal to the element's own align.h is redundant markup
    const H_ALIGN_VALUES = ['left', 'center', 'right', 'justify'];
    const V_ALIGN_VALUES = ['top', 'middle', 'bottom'];

    function canonicalHAlign(v) {
        if (typeof v !== 'string' || !v) return null;
        const norm = v.trim().toLowerCase();
        if (norm === 'start') return 'left';
        if (norm === 'end') return 'right';
        return H_ALIGN_VALUES.includes(norm) ? norm : null;
    }

    function captureAlign(domEl) {
        const st = domEl.style || {};
        const h = canonicalHAlign(st.textAlign) || 'left';
        const isColumnFlex = st.display === 'flex' && st.flexDirection === 'column'
            && ['flex-start', 'center', 'flex-end'].includes(st.justifyContent);
        let v = 'top';
        if (isColumnFlex) {
            v = st.justifyContent === 'flex-start' ? 'top'
                : st.justifyContent === 'flex-end' ? 'bottom'
                : 'middle';
        }
        return { h, v };
    }

    function stripMatchingTextAlign(html, value) {
        if (!html || !value) return html;
        const aliases = new Set([
            value,
            value === 'left' ? 'start' : value === 'right' ? 'end' : null,
            value === 'start' ? 'left' : value === 'end' ? 'right' : null,
        ].filter(Boolean));
        return html.replace(/(<[a-z]+\b[^>]*)\bstyle\s*=\s*"([^"]*)"/gi, (whole, open, decls) => {
            const kept = decls
                .split(';')
                .map(d => d.trim())
                .filter((d) => {
                    if (!d) return false;
                    const textAlign = /^text-align\s*:\s*(.+)$/i.exec(d);
                    if (!textAlign) return true;
                    return !aliases.has(textAlign[1].trim().toLowerCase());
                })
                .join('; ');
            const openClean = open.replace(/\s+$/, '');
            return kept ? `${openClean} style="${kept}"` : openClean;
        });
    }

    // A span that carries no attributes styles nothing, so two editor paths leave
    // markup that renders identically to its own contents: the colour button
    // drops the style attribute once the last declaration goes (app.js), and the
    // text-align strip above returns the bare tag when it empties one. Unwrap
    // those, and drop the spans left with nothing inside – text deleted out of a
    // styled run leaves the shell behind. Only a span whose sole attribute is an
    // emptied style counts as bare, so a class/data-latex span is never touched.
    function isBareSpan(attrs) {
        const rest = String(attrs == null ? '' : attrs).trim();
        return rest === '' || /^style\s*=\s*(?:"|'|''|"\s*"|'\s*')?$/i.test(rest);
    }

    // Depth-tracked so nested spans unwrap independently. Only the tag tokens are
    // conditional: everything between them is copied through, so a bare span's
    // text, a <br>, or a lone space inside it all survive the unwrap.
    function unwrapBareSpans(html) {
        const tokens = /<span\b([^>]*)>|<\/span>/gi;
        const stack = [];
        let out = '';
        let last = 0;
        let m;
        while ((m = tokens.exec(html)) !== null) {
            out += html.slice(last, m.index);
            last = tokens.lastIndex;
            if (m[0].charAt(1) === '/') {
                if (!stack.length) { out += m[0]; continue; }
                out += stack.pop() ? '' : m[0];
            } else {
                const bare = isBareSpan(m[1]);
                stack.push(bare);
                if (!bare) out += m[0];
            }
        }
        return out + html.slice(last);
    }

    // Removing a span can leave its parent empty, so the two passes alternate
    // until the markup settles; the cap is only a guard against a pathological
    // input, four is far more than the nesting depth can need.
    function pruneRedundantSpans(html) {
        let out = typeof html === 'string' ? html : '';
        for (let pass = 0; pass < 4; pass++) {
            const next = unwrapBareSpans(out.replace(/<span\b[^>]*><\/span>/gi, ''));
            if (next === out) break;
            out = next;
        }
        return out;
    }

    // Rich text for one block leaf (a bullet <li>, a composite's label/text,
    // a single text element). Fold style into a wrapping
    // <p style="text-align:…>"> so per-line caret alignment survives
    function leafHtml(leafEl, alignH) {
        const leafAlign = leafEl && leafEl.style ? canonicalHAlign(leafEl.style.textAlign) : null;
        // Pruning runs after the strip, so a bare span the strip creates is
        // unwrapped in this same save rather than one save later.
        const inner = pruneRedundantSpans(leafEl ? stripMatchingTextAlign(leafEl.innerHTML, alignH) : '');
        if (leafAlign && leafAlign !== alignH && !/^<(p|li)\b/i.test(inner)) {
            return `<p style="text-align:${leafAlign}">${inner}</p>`;
        }
        return inner;
    }

    // Valign is a flex column, so a single text box's inline flow must stay
    // ONE flex item – wrapped on click, re-added on re-render, peeled on capture.
    const INLINE_TAGS = new Set(['SPAN', 'EM', 'STRONG', 'B', 'I', 'U', 'S', 'A', 'SUB', 'SUP', 'CODE', 'MARK', 'SMALL', 'BR', 'Q', 'ABBR', 'TIME', 'KBD', 'SAMP', 'CITE']);

    function hasInlineLeaves(domEl) {
        for (const node of domEl.childNodes || []) {
            if (node.nodeType === 3) {
                if ((node.nodeValue || '').replace(/\s+/g, '')) return true;
            } else if (node.nodeType === 1 && INLINE_TAGS.has(node.tagName)) {
                return true;
            }
        }
        return false;
    }

    function wrapValignContent(domEl) {
        if (!domEl || !domEl.firstElementChild || !hasInlineLeaves(domEl)) return;
        const first = domEl.firstElementChild;
        if (first.classList && first.classList.contains('el__va-wrap')) return;
        const wrap = document.createElement('div');
        wrap.className = 'el__va-wrap';
        wrap.style.width = '100%';
        while (domEl.firstChild) wrap.appendChild(domEl.firstChild);
        domEl.appendChild(wrap);
    }

    function unwrapValignContent(domEl) {
        if (!domEl) return;
        const wrap = Array.from(domEl.children || []).find(c => c.classList && c.classList.contains('el__va-wrap'));
        if (!wrap) return;
        while (wrap.firstChild) domEl.insertBefore(wrap.firstChild, wrap);
        wrap.remove();
    }

    // Alignment capturing reads the box itself; the content record is built
    // from the inner leaf.
    function contentLeaf(domEl) {
        const kids = domEl && domEl.children;
        if (kids && kids.length === 1) {
            const only = kids[0];
            if (only && only.classList && only.classList.contains('el__va-wrap')) return only;
        }
        return domEl;
    }

    const ALIGNMENT_STYLE_KEYS = ['textAlign', 'display', 'flexDirection', 'justifyContent'];

    // Inline styling the toolbar puts on added text/shapes that has to survive
    // into the model's style_overrides (boxStyle serialises these kebab-case).
    const STYLE_PROPS = [
        'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'color',
        'letterSpacing', 'lineHeight', 'padding', 'background',
        'backgroundColor', 'backgroundImage', 'borderRadius', 'border',
        'borderLeft', 'borderTop', 'borderRight', 'borderBottom', 'opacity',
        'minWidth', 'minHeight',
        // A picture's crop (see "Cropped pictures" in app.js).
        'clipPath',
    ];

    // How a picture fills its box. The editor changes it on the DOM - a
    // resized or cropped picture becomes "fill" - so it is read back from
    // there rather than assumed.
    const IMAGE_FITS = ['cover', 'contain', 'fill'];
    function captureImageFit(domEl, next) {
        const mode = (domEl.dataset && domEl.dataset.objectFit) || (domEl.style && domEl.style.objectFit);
        if (IMAGE_FITS.includes(mode)) next.image_fit = { ...(next.image_fit || {}), mode };
    }

    // Reveal-on-click is set on the DOM by the editor's context menu. A list
    // revealed bullet by bullet carries its place in the queue on the list
    // (data-reveal-items); anything else carries it on itself (data-reveal).
    function captureReveal(domEl, next) {
        const data = domEl.dataset || {};
        const items = Number(data.revealItems);
        const own = Number(data.reveal);
        delete next.reveal;
        delete next.reveal_items;
        if (items > 0) { next.reveal = items; next.reveal_items = true; }
        else if (own > 0) next.reveal = own;
    }

    function captureOverrides(domEl) {
        const out = {};
        if (!domEl.style) return out;
        for (const prop of STYLE_PROPS) {
            const v = domEl.style[prop];
            if (v) out[prop] = v;
        }
        return out;
    }

    // Text for an element comes out of the DOM with KaTeX spans reverted to
    // $…$ source first, so the stored contents_json stays in the same
    // vocabulary buildSlideModel produced. Rich text (line breaks, span
    // styling) is stored as its innerHTML – the server's write-path sanitizer
    // is what keeps it to the allowlisted p/br/span[style] vocabulary.
    // alignH (the element's own box-level horizontal alignment, if any) lets
    // equal run-level text-align be dropped as redundant markup.
    function extractContent(domEl, type, alignH) {
        if (type === 'bullet_list') {
            const items = Array.from(domEl.children)
                .filter(c => c.tagName === 'LI')
                .map(li => leafHtml(li, alignH));
            return { items };
        }
        if (type === 'flow_step' || type === 'timeline_event') {
            const label = domEl.querySelector('.el__label');
            const text = domEl.querySelector('.el__text');
            return {
                label: leafHtml(label, alignH),
                text: leafHtml(text, alignH),
            };
        }
        if (type === 'term_copy') {
            const term = domEl.querySelector('.el__term');
            const def = domEl.querySelector('.el__definition');
            return {
                term: leafHtml(term, alignH),
                definition: leafHtml(def, alignH),
            };
        }
        if (type === 'example_box') {
            const text = domEl.querySelector('.el__example-text');
            return { text: leafHtml(text || domEl, alignH) };
        }
        return { text: leafHtml(contentLeaf(domEl), alignH) };
    }

    // Re-read geometry + styles + content for an element the model already knows.
    function updateExisting(prev, domEl, contents, slideEl) {
        const type = prev.type;
        const cid = prev.content_id;

        const next = clone(prev);
        // Group membership is stamped on the element as data-group-id by the
        // renderer. Keep it in sync here so a drag (or an ungroup that strips
        // the attribute) is reflected in the model's `group` key.
        const gid = domEl.dataset && domEl.dataset.groupId;
        next.group = (typeof gid === 'string' && gid) ? gid : null;
        // A hidden element has no layout box – never clobber its design geometry.
        if (!domEl.hidden) {
            const pos = domPos(domEl, slideEl);
            next.box.x = Math.round(pos.left);
            next.box.y = Math.round(pos.top);
            next.box.width = Math.round(pos.width);
            next.box.height = Math.round(pos.height);
            next.z_index = readZ(domEl);
        }
        next.visible = !domEl.hidden;

        // translate on the DOM makes every later sync re-add it to the stored
        // position, drifting the box one more delta per save.
        if (domEl.style.transform) {
            const kept = domEl.style.transform.replace(/translate\([^)]*\)\s*/g, '').trim();
            domEl.style.transform = kept;
        }

        // Whole-element alignment (box-level align buttons) is captured into
        // the dedicated align key and read before the content below, so equal
        // run-level text-align in the innerHTML is stripped as redundant.
        const align = (type === 'image' || type === 'shape') ? null : captureAlign(domEl);
        const alignH = align ? align.h : null;

        // Whole-element styling edits (colour A button, shape background). Capture
        // is faithful for every style key the editor owns; the alignment keys are
        // deliberately excluded by STYLE_PROPS, because the same DOM values were
        // just read into the align key above (textAlign -> align.h, the
        // flex-column justify triple -> align.v) and must not be stored twice.
        const style = domEl.style;
        if (style) {
            const previousOverrides = prev.style_overrides && typeof prev.style_overrides === 'object'
                ? prev.style_overrides : {};
            const freshest = captureOverrides(domEl);
            if (Object.keys(freshest).length) {
                next.style_overrides = { ...clone(previousOverrides), ...freshest };
            } else if (align) {
                next.style_overrides = clone(previousOverrides);
            }
        }
        if (align) next.align = { h: align.h, v: align.v };
        else delete next.align;
        captureReveal(domEl, next);
        if (type === 'image') {
            captureImageFit(domEl, next);
            // Overrides are merged onto what was stored, so a crop taken off
            // the DOM has to be taken off here by hand or it would come back.
            if (next.style_overrides && !(style && style.clipPath)) delete next.style_overrides.clipPath;
        }

        if (cid && contents[cid]) {
            if (type === 'image') {
                const c = contents[cid];
                c.url = domEl.currentSrc || domEl.src || c.url || '';
                const kw = domEl.getAttribute('alt');
                if (kw) c.keyword = kw;
                else if ('keyword' in c) delete c.keyword;
                const id = domEl.dataset.unsplashId;
                const name = domEl.dataset.unsplashCredit;
                const profile = domEl.dataset.unsplashProfile;
                if (name) c.credit = { id: id || '', name, profileUrl: profile || '' };
                else delete c.credit;
                // A custom upload owns a CDN file through its imagePath. Keep
                // it in the content record so the image stays managed (and its
                // file deletable) after a reload re-renders it from the model.
                const imagePath = domEl.dataset.imagePath;
                if (imagePath) c.imagePath = imagePath;
                else if ('imagePath' in c) delete c.imagePath;
            } else {
                // Content round-trips as $…$ source, not rendered KaTeX.
                const hadMath = MathRender.revertMathInElement && MathRender.revertMathInElement(domEl);
                const extracted = extractContent(domEl, type, alignH);
                contents[cid] = { ...contents[cid], ...extracted };
                if (hadMath && MathRender.renderMathInElement) MathRender.renderMathInElement(domEl);
            }
        }

        return next;
    }

    // Turn a DOM element the model doesn't know (toolbar-added, or a duplicate
    // of a known one) into a brand-new model element + content record.
    function handleAdded(domEl, opts, contents, usedElIds, usedContentIds, slideEl) {
        const pos = domEl.hidden ? null : domPos(domEl, slideEl);
        const isImage = domEl.tagName === 'IMG' || domEl.className.indexOf('added-image') !== -1;
        const isShape = !isImage && domEl.className.indexOf('added-shape') !== -1;
        const isTextual = !isImage && !isShape;

        // Box-level alignment lands on the element's own align key; shapes and
        // images never carry it (an image fills its box, a shape is geometry).
        const align = isTextual ? captureAlign(domEl) : null;
        const overrides = opts.copyFrom
            ? clone(opts.copyFrom.style_overrides || {})
            : captureOverrides(domEl);

        let type, content, contentId;
        if (opts.copyFrom) {
            // Duplicate: independent copy of the original element + its content.
            type = opts.copyFrom.type;
            const srcId = opts.copyFrom.content_id;
            if (srcId && contents[srcId]) {
                contentId = uniqueId(`${srcId}_copy`, usedContentIds);
                contents[contentId] = clone(contents[srcId]);
            } else {
                contentId = null;
            }
        } else if (isImage) {
            type = 'image';
            content = {
                url: domEl.currentSrc || domEl.src || '',
                keyword: domEl.alt || '',
            };
            const id = domEl.dataset.unsplashId;
            const name = domEl.dataset.unsplashCredit;
            if (name) content.credit = { id: id || '', name, profileUrl: domEl.dataset.unsplashProfile || '' };
            // Custom uploads carry the CDN storage path on the DOM; keep it in
            // the record (and the image gets its own element record below) so
            // the file stays tracked for delete/reuse across re-renders.
            if (domEl.dataset?.imagePath) content.imagePath = domEl.dataset.imagePath;
            contentId = uniqueId('cnt_image', usedContentIds);
            contents[contentId] = content;
        } else if (isShape) {
            // Shapes carry their look in the element's own style_overrides
            // (background, borderRadius…) and never take a contents_json record.
            type = 'shape';
            contentId = null;
        } else {
            type = 'text';
            contentId = uniqueId('cnt_text', usedContentIds);
            contents[contentId] = extractContent(domEl, 'default', align ? align.h : null);
        }

        const baseId = opts.copyFrom ? opts.copyFrom.id : (type === 'image' ? 'el_image' : 'el_added');
        const id = uniqueId(baseId, usedElIds);

        const next = {
            id,
            type,
            content_id: contentId,
            box: {
                x: pos ? Math.round(pos.left) : 0,
                y: pos ? Math.round(pos.top) : 0,
                width: pos ? Math.round(pos.width) : 240,
                height: pos ? Math.round(pos.height) : 0,
            },
            z_index: readZ(domEl),
            style_overrides: overrides,
            visible: !domEl.hidden,
            transform: { rotation: 0, flipH: false, flipV: false },
            group: null,
            ...(align ? { align: { h: align.h, v: align.v } } : {}),
        };
        if (type === 'image') {
            next.image_fit = opts.copyFrom
                ? clone(opts.copyFrom.image_fit || { mode: 'contain' })
                : { mode: 'cover' };
        }
        next.group = domEl.dataset?.groupId || null;
        captureReveal(domEl, next);
        if (type === 'image' && !opts.copyFrom) captureImageFit(domEl, next);
        // The slide's own background colour: a full-canvas shape the editor
        // adds from the Theme menu, kept out of the way of clicks.
        if (type === 'shape' && domEl.hasAttribute('data-slide-bg')) next.role = 'background';
        return next;
    }

    /**
     * Rewrites a slide model from its rendered DOM.
     * @param {{contents?: Object, elements?: Array}} model previous model
     * @param {Element} container stage slide container (.slide lives inside)
     * @returns {null | {contents: Object, elements: Array}} new model, or null
     *   when the container holds no slide.
     */
    function syncFromDom(model, container) {
        const slideEl = container.querySelector('.slide');
        if (!slideEl) return null;

        const prevElements = Array.isArray(model.elements) ? model.elements : [];
        const prevContents = (model.contents && typeof model.contents === 'object') ? model.contents : {};

        const contents = clone(prevContents);
        const usedElIds = new Set();
        const usedContentIds = new Set();
        prevElements.forEach(e => { if (e.id) usedElIds.add(e.id); });
        Object.keys(contents).forEach(k => usedContentIds.add(k));

        const members = [...slideEl.children];
        slideEl.querySelectorAll('.slide__group:not([data-repeat-ghost]) > *').forEach(el => members.push(el));

        const elements = [];
        const seenIds = new Set();

        for (const domEl of members) {
            if (domEl.hasAttribute('data-repeat-ghost')) continue;
            if (domEl.classList.contains('slide__group')) continue;
            if (domEl.closest('[data-slot-repeat]')) continue;

            const id = domEl.dataset && domEl.dataset.elId;

            // An element with a data-el-id matches a known element – update 
            // it instead of creating a duplicate.
            if (id) {
                if (!seenIds.has(id)) {
                    seenIds.add(id);
                    const prev = prevElements.find(e => e.id === id);
                    if (prev) {
                        elements.push(updateExisting(prev, domEl, contents, slideEl));
                    } else {
                        elements.push(handleAdded(domEl, {}, contents, usedElIds, usedContentIds, slideEl));
                    }
                } else {
                    // Duplicate of a known element: fresh independent copy.
                    const original = prevElements.find(e => e.id === id);
                    elements.push(handleAdded(domEl, { copyFrom: original || null }, contents, usedElIds, usedContentIds, slideEl));
                }
                continue;
            }

            // Toolbar-added element without a model identity.
            const added = handleAdded(domEl, {}, contents, usedElIds, usedContentIds, slideEl);
            if (added) {
                // Stamp the id so the next sync recognises this element and
                // updates it instead of creating another duplicate.
                domEl.setAttribute('data-el-id', added.id);
                elements.push(added);
            }
        }

        // Drop content records when not referenced by any element record
        const liveContentIds = new Set();
        elements.forEach(e => { if (e && e.content_id) liveContentIds.add(e.content_id); });
        Object.keys(contents).forEach(k => {
            if (!liveContentIds.has(k)) delete contents[k];
        });

        return { contents, elements };
    }

    /**
     * Applies AI-edit model operations to a slide model.
     *
     * Pure JSON-to-JSON (no DOM) so it works for the AI edit path, where the
     * target slide may not be the one currently on stage. The server has already
     * sanitized content records and resolved image placeholders; this only has
     * to merge the edits sensibly: set_content updates the one content record,
     * set_style merges camelCase overrides, set_align writes the element's own
     * align key, set_box anchors the geometry, and remove_element drops
     * orphaned content after it.
     *
     * Alignment is the one thing that may not arrive as CSS. An op carrying
     * textAlign / display / flexDirection / justifyContent in its style is
     * rejected whole rather than half-applied, so a box can never end up with
     * align and a competing style_overrides declaration; the caller reports it
     * from `rejected` so the request can be re-asked against set_align.
     * @param {Object} contents contents_json (content_id → record)
     * @param {Array} elements elements_json
     * @param {Array} ops the edit operations from POST /edit/model
     * @returns {{contents: Object, elements: Array, changed: boolean, rejected: Array}}
     */
    function applyModelOps(contents, elements, ops) {
        const outContents = clone(contents);
        const outElements = clone(elements);
        outElements.forEach(element => {
            if (element && !Object.prototype.hasOwnProperty.call(element, 'group')) element.group = null;
        });
        const knownIds = new Set(outElements.map(e => e && e.id).filter(Boolean));
        const knownContentIds = new Set(Object.keys(outContents));
        const opList = Array.isArray(ops) ? ops : [];
        const rejected = [];
        let changed = false;

        function elById(id) {
            return typeof id === 'string' ? outElements.find(e => e && e.id === id) : undefined;
        }

        // Alignment keys present in a proposed style object, if any.
        function alignmentKeysIn(styleObj) {
            if (!styleObj || typeof styleObj !== 'object') return [];
            return Object.keys(styleObj).filter(k => ALIGNMENT_STYLE_KEYS.includes(k));
        }

        function rejectOp(index, op, keys) {
            rejected.push({
                index,
                action: op.action || null,
                element: typeof op.element === 'string' ? op.element : null,
                keys,
            });
        }

        // The canonical alignment write, shared by set_align and the align field
        // on set_style. Returns false when the element cannot carry align.
        function applyAlignTo(el, requested) {
            if (!el || el.type === 'image' || el.type === 'shape') return false;
            if (!requested || typeof requested !== 'object') return false;
            const current = el.align && typeof el.align === 'object' ? el.align : {};
            const h = canonicalHAlign(requested.h);
            const v = V_ALIGN_VALUES.includes(requested.v) ? requested.v : null;
            el.align = {
                h: h || (H_ALIGN_VALUES.includes(current.h) ? current.h : 'left'),
                v: v || (V_ALIGN_VALUES.includes(current.v) ? current.v : 'top'),
            };
            return true;
        }

        for (let opIndex = 0; opIndex < opList.length; opIndex++) {
            const op = opList[opIndex];
            if (!op || typeof op !== 'object') continue;
            switch (op.action) {
                case 'set_content': {
                    const el = elById(op.element);
                    if (!el || !op.fields || typeof op.fields !== 'object') continue;
                    if (!el.content_id || !knownContentIds.has(el.content_id)) continue;
                    outContents[el.content_id] = { ...outContents[el.content_id], ...clone(op.fields) };
                    changed = true;
                    break;
                }
                case 'set_style': {
                    const el = elById(op.element);
                    if (!el) continue;
                    const hasStyle = op.style && typeof op.style === 'object';
                    const hasAlign = op.align && typeof op.align === 'object';
                    if (!hasStyle && !hasAlign) continue;
                    if (hasStyle) {
                        const banned = alignmentKeysIn(op.style);
                        if (banned.length) { rejectOp(opIndex, op, banned); continue; }
                    }
                    if (hasAlign && applyAlignTo(el, op.align)) changed = true;
                    if (hasStyle) {
                        el.style_overrides = { ...(el.style_overrides || {}), ...clone(op.style) };
                        changed = true;
                    }
                    break;
                }
                case 'set_align': {
                    const el = elById(op.element);
                    if (!el) continue;
                    if (applyAlignTo(el, op.align)) changed = true;
                    break;
                }
                case 'set_box': {
                    const el = elById(op.element);
                    if (!el || !op.box || typeof op.box !== 'object') continue;
                    // A group resizes as one object: when one member's box is
                    // re-anchored, every member takes the same delta, so all
                    // members scale together and their relative offsets are
                    // preserved (PowerPoint-style grouped resize).
                    const newBox = {
                        x: Math.round(Number(op.box.x)),
                        y: Math.round(Number(op.box.y)),
                        width: Math.round(Number(op.box.width)),
                        height: Math.round(Number(op.box.height)),
                    };
                    const gid = typeof el.group === 'string' ? el.group : null;
                    if (gid) {
                        const dX = newBox.x - el.box.x;
                        const dY = newBox.y - el.box.y;
                        const dW = newBox.width - el.box.width;
                        const dH = newBox.height - el.box.height;
                        for (const member of outElements) {
                            if (!member || member === el) continue;
                            if (member.group !== gid) continue;
                            member.box = {
                                x: Math.round(member.box.x + dX),
                                y: Math.round(member.box.y + dY),
                                // Clamp relative to each member's own size so the
                                // MIN guard never inflates a slim member (e.g. a
                                // 5px accent bar) when its delta in that axis is 0.
                                width: Math.max(Math.min(20, member.box.width), Math.round(member.box.width + dW)),
                                height: Math.max(Math.min(20, member.box.height), Math.round(member.box.height + dH)),
                            };
                        }
                    }
                    el.box = newBox;
                    changed = true;
                    break;
                }
                case 'set_visible': {
                    const el = elById(op.element);
                    if (!el) continue;
                    el.visible = op.visible === true;
                    changed = true;
                    break;
                }
                case 'remove_element': {
                    const idx = outElements.findIndex(e => e && e.id === op.element);
                    if (idx === -1) continue;
                    const removed = outElements.splice(idx, 1)[0];
                    if (removed && removed.content_id && outContents[removed.content_id]
                        && !outElements.some(e => e && e.content_id === removed.content_id)) {
                        delete outContents[removed.content_id];
                        knownContentIds.delete(removed.content_id);
                    }
                    if (removed) knownIds.delete(removed.id);
                    changed = true;
                    break;
                }
                case 'add_element': {
                    // The tool schema calls the visual merge "style" (set_style);
                    // accept the raw op.style too so a shape's look survives.
                    const styleSrc = (op.style && typeof op.style === 'object') ? op.style : null;
                    const overrideSrc = (op.style_overrides && typeof op.style_overrides === 'object') ? op.style_overrides : null;
                    // Reject before any mutation, so a refused add_element cannot
                    // leave a content record or a consumed id behind.
                    const banned = [...alignmentKeysIn(styleSrc), ...alignmentKeysIn(overrideSrc)];
                    if (banned.length) {
                        rejectOp(opIndex, op, [...new Set(banned)]);
                        break;
                    }
                    const box = (op.box && typeof op.box === 'object') ? op.box : {};
                    const type = typeof op.type === 'string' && op.type ? op.type : 'text';
                    const baseId = typeof op.element === 'string' && op.element ? op.element : `el_${type}`;
                    const id = uniqueId(baseId, knownIds);
                    knownIds.add(id);

                    // Shapes are pure geometry – they never get a contents record,
                    // even if the model offered a bogus {text:''} content block.
                    const isShape = type === 'shape';
                    const record = (op.content && typeof op.content === 'object') ? op.content : {};
                    let contentId = null;
                    if (!isShape && (Object.keys(record).length || type === 'image')) {
                        contentId = uniqueId(type === 'image' ? 'cnt_image' : `cnt_${type}`, knownContentIds);
                        knownContentIds.add(contentId);
                        outContents[contentId] = clone(record);
                    }

                    const style = styleSrc || overrideSrc;
                    const isTextual = type !== 'shape' && type !== 'image';
                    const overrides = style ? clone(style) : {};
                    let align = null;
                    if (isTextual) {
                        const requested = op.align && typeof op.align === 'object' ? op.align : {};
                        const h = canonicalHAlign(requested.h);
                        const v = V_ALIGN_VALUES.includes(requested.v) ? requested.v : null;
                        align = { h: h || 'left', v: v || 'top' };
                    }

                    const el = {
                        id,
                        type,
                        content_id: contentId,
                        box: {
                            x: Math.round(Number(box.x) || 0),
                            y: Math.round(Number(box.y) || 0),
                            width: Math.round(Number(box.width) || 240),
                            height: Math.round(Number(box.height) || (type === 'image' ? 160 : 44)),
                        },
                        z_index: Number.isInteger(op.z_index) ? op.z_index : 10,
                        style_overrides: overrides,
                        visible: op.visible !== false,
                        transform: { rotation: 0, flipH: false, flipV: false },
                        group: null,
                        ...(align ? { align } : {}),
                    };
                    // A new element may be dropped straight into an existing
                    // group by reusing its id ("g3"), so it moves with the
                    // rest of the badge until the teacher ungroups it.
                    if (op.group != null && (typeof op.group === 'string' || typeof op.group === 'number')) {
                        el.group = String(op.group);
                    }
                    if (type === 'image') {
                        el.image_fit = op.image_fit && typeof op.image_fit === 'object' && op.image_fit.mode
                            ? clone(op.image_fit)
                            : { mode: 'cover' };
                    }
                    outElements.push(el);
                    changed = true;
                    break;
                }
            }
        }
        return { contents: outContents, elements: outElements, changed, rejected };
    }

    return { syncFromDom, applyModelOps, clone, wrapValignContent, unwrapValignContent, hasInlineLeaves };
})();

if (typeof module === 'object' && module.exports) {
    module.exports = SlideModelEditor;
}